import {
  Badge,
  Drawer,
  Fieldset,
  Group,
  Stack,
  Text,
  Title,
} from "@mantine/core"
import {
  CancelButton,
  DeleteButtonWithConfirmModal,
  SaveButton,
} from "@components/Buttons"
import { Form } from "@components/typed-form"
import { SuspenseWithErrorBoundary } from "@components/suspense"
import { RenderActivity, SharedControls } from "./shared-modal-fields"
import { useTranslation } from "react-i18next"
import { UseFormReturnType } from "@mantine/form"
import { DefaultError } from "@components/error-context"
import {
  hasValidationErrors,
  tryGetValidationErrorsAsMantineErrors,
} from "@/utils/to-mantine-validation-errors"
import { useSuspenseQueryGet } from "@/typed-client/hooks/use-suspense-query-get"
import { usePostMutation } from "@/typed-client/hooks/use-post-mutation"
import { useBatchedCastStatus } from "@/-dataloaders/cast-status"
import {
  AddCastRequest,
  EditCastRequest,
} from "@/@client/Theasoft_ApiServer_Orchestra"
import { ErrorAlert } from "@/blocks/errors/error-alert"
import { RenderSimplePerson } from "@/blocks/persons-and-users/User"
import { showErrorNotification } from "@/utils/form-submission-notifications"

export type EditingCast = {
  type: "edit"
  contractId: number
  castId: number
  activityId: number
  workareaId: number
}

export function EditCastModal(props: {
  coordinate: EditingCast | null
  onCancel: () => void
  onEdited: () => void
  onDeleted: () => void
}) {
  const { t } = useTranslation("app_server")
  return (
    <Drawer
      position="right"
      overlayProps={{ backgroundOpacity: 0.5, blur: 0 }}
      opened={props.coordinate !== null}
      onClose={() => props.onCancel()}
      title={<Title order={3}>{t("Cast")}</Title>}
    >
      <SuspenseWithErrorBoundary>
        {props.coordinate !== null ? (
          <EditContent
            coordinate={props.coordinate}
            onEdited={props.onEdited}
            onDeleted={props.onDeleted}
            onCancel={props.onCancel}
          />
        ) : null}
      </SuspenseWithErrorBoundary>
    </Drawer>
  )
}

function EditContent({
  coordinate,
  onEdited,
  onDeleted,
  onCancel,
}: {
  coordinate: EditingCast
  onEdited: () => void
  onDeleted: () => void
  onCancel: () => void
}) {
  const {
    data: [castWithPermission, _],
    refetch,
  } = useSuspenseQueryGet("/api/Orchestra/GetCast", {
    castId: coordinate.castId,
    contractId: coordinate.contractId,
    workareaId: coordinate.workareaId,
  })
  const { value: cast, permission } = castWithPermission
  const deleteCast = usePostMutation("/api/Orchestra/DeleteCast", {
    onSuccess: () => onEdited(),
  })
  const { data: castStatus } = useBatchedCastStatus(
    castWithPermission.value.castStatusId
  )

  const isReadOnly = permission === "ReadOnly"
  const { t } = useTranslation("app_server")
  if (permission === "NotAllowed") {
    // this should never happen (only if server did not filter data properly)
    return <ErrorAlert error={t("InternalServerError")} />
  }
  const initialValues: EditCastRequest = {
    castId: coordinate.castId,
    properties: {
      roleId: cast.roleId,
      creditableValue: cast.creditableValue,
      isCover: cast.isCover,
      // isSecret: cast.isSecret,
      isReplacement: cast.isReplacement,
      isCanceled: cast.isCanceled,
      note: cast.note,
      castOrderIndex: 0,
      color: cast.color,
    },
  }
  return (
    <Stack>
      <Badge>
        {t("CastStatus")}: {castStatus?.name}
      </Badge>
      <Form
        onError={(e, _, __, f) => {
          if (e instanceof DefaultError && hasValidationErrors(e)) {
            const validationErrors = tryGetValidationErrorsAsMantineErrors(e, t)
            if (validationErrors) {
              f.setErrors(validationErrors)
            }
          }
          showErrorNotification(e)
        }}
        url="/api/Orchestra/EditCast"
        initialValues={initialValues}
        onSuccess={() => {
          refetch()
          onEdited()
        }}
        transform={(v) => v}
      >
        {(f, _, m) => (
          <>
            <Fieldset disabled={permission === "ReadOnly"} variant="unstyled">
              <Stack>
                <RenderActivity activity={cast.activity} />
                <Text fw={700}>
                  <RenderSimplePerson suspense personId={cast.personId} />
                </Text>
                <SharedControls
                  // its difficult to satisfy typescript in this case
                  f={f as UseFormReturnType<AddCastRequest | EditCastRequest>}
                  _={_.properties}
                  workareaId={coordinate.workareaId}
                  activityId={cast.activityId}
                  contractId={cast.contractId ? cast.contractId : 0}
                />
              </Stack>
            </Fieldset>
            <Group justify="space-between" mt="md">
              <CancelButton onClick={onCancel} />
              <DeleteButtonWithConfirmModal
                disabled={isReadOnly}
                confirmModalProps={{
                  confirmProps: { loading: deleteCast.isPending },
                  onConfirm: () =>
                    deleteCast.mutate(
                      { castId: coordinate.castId },
                      {
                        onSuccess: () => onDeleted(),
                        onError: showErrorNotification,
                      }
                    ),
                }}
              />

              <SaveButton
                disabled={permission === "ReadOnly"}
                loading={m.isPending}
              />
            </Group>
          </>
        )}
      </Form>
    </Stack>
  )
}
